import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, BookOpen, Trophy } from 'lucide-react';
import content from '../../data/content.json';

const GradesAndBoards = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <section className="py-20 bg-gray-50 dark:bg-gray-800">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={containerVariants}
        >
          <motion.div variants={itemVariants} className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-6">
              {content.grades.title}
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
              Comprehensive tutoring from preschool to graduation across all major educational boards
            </p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {content.grades.levels.map((level, index) => {
              const icons = [GraduationCap, BookOpen, Trophy];
              const IconComponent = icons[index];
              return (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ y: -5 }}
                  className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg text-center"
                >
                  <div className="w-20 h-20 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-3">
                    {level.category}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400 text-lg">
                    {level.grades}
                  </p>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            variants={itemVariants}
            className="bg-gradient-to-br from-purple-100 to-pink-100 dark:from-purple-900/30 dark:to-pink-900/30 rounded-2xl p-8 md:p-12"
          >
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {content.boards.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300">
                We provide expert guidance for all major educational boards in India and internationally
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-4">
              {content.boards.list.map((board, index) => (
                <motion.div
                  key={index}
                  whileHover={{ scale: 1.05 }}
                  className="px-6 py-3 bg-white dark:bg-gray-800 rounded-lg shadow-md"
                >
                  <span className="text-lg font-semibold text-purple-600 dark:text-purple-400">
                    {board}
                  </span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="mt-12">
            <div className="text-center mb-8">
              <h3 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
                {content.competitive.title}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                Specialized coaching for competitive exams to give your child an early advantage
              </p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {content.competitive.courses.map((course, index) => (
                <motion.div
                  key={index}
                  variants={itemVariants}
                  whileHover={{ y: -5 }}
                  className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-md"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center mb-4">
                    <Trophy className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    {course.name}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {course.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default GradesAndBoards;
